import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import Tooltip from 'Components/Molecules/Tooltip/Tooltip';

const Check = () => (
    <svg stroke="currentcolor" width="12" height="12" fill="none" viewBox="0 0 12 12">
        <path strokeLinecap="round" strokeWidth="2" d="M2 6l2.894 2.894a.15.15 0 00.212 0L11 3"></path>
    </svg>
);

const Indeterminate = () => (
    <svg strokeLinejoin="round" stroke="currentcolor" strokeWidth="4" viewBox="0 0 24 24">
        <path d="M21 12L3 12" />
    </svg>
);

const Checkbox = forwardRef((props, ref) => {
    const { indeterminate, className, checked, children, fullWidth, truncate, tooltip, ...htmlProps } = props;
    const checkboxRef = React.useRef(null);

    useImperativeHandle(ref, () => checkboxRef?.current);

    useEffect(() => {
        if (indeterminate) {
            checkboxRef.current.indeterminate = true;
            checkboxRef.current.checked = false;
        } else if (checked) {
            checkboxRef.current.indeterminate = false;
            checkboxRef.current.checked = true;
        } else {
            checkboxRef.current.indeterminate = false;
            checkboxRef.current.checked = false;
        }
    }, [indeterminate, checked]);

    const handleClick = (ev) => ev.stopPropagation();

    const labelContent = (
        <label
            onClick={handleClick}
            className={cn(
                'inline-flex items-center cursor-pointer gap-2',
                (fullWidth || truncate) && 'w-full',
                className,
            )}
        >
            <span className="relative flex w-fit shrink-0">
                <input
                    className={cn(
                        'bg-white appearance-none size-5 border-1 border-gray-300 rounded shadow-sm transition-all cursor-pointer text-dark-blue',
                        'focus-visible:outline-none focus-visible:ring-2 ring-primary/20 focus-visible:border-primary',
                        'disabled:bg-gray-100 disabled:border-gray-200 disabled:cursor-not-allowed',
                    )}
                    type="checkbox"
                    ref={checkboxRef}
                    checked={checked}
                    {...htmlProps}
                />

                <span
                    aria-hidden="true"
                    className={cn(
                        'absolute pointer-events-none flex items-center justify-center left-0 top-0 text-dark-blue',
                        'size-5 px-[3px]',
                    )}
                >
                    {indeterminate ? <Indeterminate /> : checked && <Check />}
                </span>
            </span>

            {children && <span className={cn('grow min-w-0', truncate && 'truncate')}>{children}</span>}
        </label>
    );

    if (tooltip) {
        return <Tooltip label={tooltip}>{labelContent}</Tooltip>;
    }

    return labelContent;
});

export default Checkbox;

Checkbox.displayName = 'Checkbox';

Checkbox.propTypes = {
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func,
    name: PropTypes.string,
    className: PropTypes.string,
    children: PropTypes.node,
    fullWidth: PropTypes.bool,
    truncate: PropTypes.bool,
    tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};
